// modules/approvals/components/RequestDetailsCard.jsx
//
// "What is this approval actually for?"
//
// The approval screens used to show only the workflow (steps + history), so an
// approver looking at e.g. JOURNAL_ENTRY_ACTION_APPROVAL saw a raw ObjectId and
// nothing about the entry being reversed. This panel renders the business ask:
// the entity type's registered columns resolved against the request's
// displayMeta, plus an optional deep link to the source record and a collapsed
// view of the rule-engine facts that selected this workflow.
//
// It is entity-type agnostic — everything comes from WorkflowRegistry metadata
// served by GET /approval/entity-types, with a displayMeta fallback when that
// metadata is unavailable.

import React from 'react';
import { Card, Descriptions, Tag, Button, Collapse, Typography, Empty, Space } from 'antd';
import { FileSearchOutlined, ExportOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  buildDetailRows,
  buildFactRows,
  resolveViewTarget,
  prettifyKey,
} from '../utils/requestDisplay';

const { Text } = Typography;

const PRIORITY_COLOR = { HIGH: 'red', CRITICAL: 'red', NORMAL: 'blue', LOW: 'default' };

const RequestDetailsCard = ({
  instance,
  entityMeta,
  showFacts = true,
  style,
  bodyStyle,
  bordered = false,
}) => {
  const navigate = useNavigate();
  if (!instance) return null;

  const entityType = instance.entityType || instance.requestId?.entityType || instance.module;
  const title = entityMeta?.displayName || prettifyKey(entityType || 'Request');
  const rows = buildDetailRows(instance, entityMeta);
  const factRows = showFacts ? buildFactRows(instance) : [];
  const target = resolveViewTarget(instance, entityMeta);
  const priority = instance.requestId?.priority;
  const templateName = instance.template?.name;

  return (
    <Card
      title={
        <Space size={8} wrap>
          <FileSearchOutlined />
          <span>{title}</span>
          {priority && (
            <Tag color={PRIORITY_COLOR[String(priority).toUpperCase()] || 'default'}>
              {String(priority).toUpperCase()}
            </Tag>
          )}
        </Space>
      }
      extra={
        target ? (
          <Button type="link" size="small" icon={<ExportOutlined />} onClick={() => navigate(target.path)}>
            {target.label}
          </Button>
        ) : null
      }
      bordered={bordered}
      style={{ borderRadius: 12, boxShadow: bordered ? 'none' : '0 4px 20px rgba(0,0,0,0.05)', ...style }}
      headStyle={{ borderBottom: '1px solid #f0f0f0', padding: '16px 24px' }}
      bodyStyle={{ padding: 24, ...bodyStyle }}
    >
      {rows.length ? (
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" bordered>
          {rows.map((r) => (
            <Descriptions.Item key={r.key} label={r.label}>
              {r.isBadge ? <Tag>{r.value}</Tag> : <Text>{r.value}</Text>}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="This request carries no display details."
        />
      )}

      {(factRows.length > 0 || templateName) && (
        <Collapse
          ghost
          style={{ marginTop: 12 }}
          items={[
            {
              key: 'facts',
              label: (
                <Text type="secondary">
                  <ExperimentOutlined /> Why this workflow
                </Text>
              ),
              children: (
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" bordered>
                  {templateName && (
                    <Descriptions.Item label="Matched Workflow">{templateName}</Descriptions.Item>
                  )}
                  {instance.workflowVersion != null && (
                    <Descriptions.Item label="Workflow Version">v{instance.workflowVersion}</Descriptions.Item>
                  )}
                  {factRows.map((f) => (
                    <Descriptions.Item key={f.key} label={f.label}>{f.value}</Descriptions.Item>
                  ))}
                </Descriptions>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
};

export default RequestDetailsCard;
